/**
 * Ground albedo texture (2026-09-16, owner direction) — a real tileable ground image under the
 * procedural ground field.
 *
 * WHY. The procedural ground (groundMaterial.ts, 2026-09-14) drew its detail from value noise, and
 * value noise is grid-aligned: at game distance its accent (3.5 m) and grain (0.45 m) cells read as
 * square blocks — "Minecraft at a distance", owner. A painted ground texture supplies that detail
 * instead. The palette keeps doing what it is good at: each biome's HUE and its large, smooth
 * patches (base ↔ patch), so the capital does not turn moss-green and the Reach keeps its soil
 * patches. The texture is tinted per biome by (palette colour ÷ the texture's own mean colour).
 *
 * PRESENTATION ONLY. Nothing in backend/ or shared/ reads the ground material: node placement reads
 * the baked terrain rasters, never the shading (terrain-sample.ts), so a texture swap moves no
 * mechanic.
 *
 * History: 2026-09-16 shipped ONE interim texture for every biome (the art machine's Azure
 * `moorlandMoss`; handover docs/art/terrain-material-candidates-2026-09-16/README.md on auto/art).
 * Tinted brown it read as cracked earth, so 2026-09-17 replaced it with a texture per palette.
 *
 * Import boundary (AD-122): frontend-local; `three` is allowed in frontend/.
 */

import * as THREE from 'three';

import { type AssetCdnFallback, sessionAssetCdnFallback } from '../assets/assetCdnFallback.js';

export interface GroundAlbedo {
  /** Served path, relative to the asset HTTP base. */
  readonly url: string;
  /** sha256 of the served file — the cache-busting version AND a drift pin (the test hashes it). */
  readonly hash: string;
  /** World metres covered by one repeat of the texture. */
  readonly tileMetres: number;
  /**
   * The texture's mean colour in LINEAR light. The shader tints by (palette colour ÷ this), so a
   * biome's palette colour is the average colour of its textured ground.
   */
  readonly meanLinear: readonly [number, number, number];
}

/**
 * One texture per ground palette (2026-09-17). Generated by the owner in ChatGPT from per-palette
 * prompts written against the Art Direction Bible (stylised, painterly-but-tidy, flat-lit albedo)
 * and each palette's own colours, each described as ~4 × 4 m of ground — hence `tileMetres: 4`.
 * Seam-checked before delivery: wrap/interior ratios 1.5–2.3, no visible seam at full resolution.
 * Sources kept in `art-inbox/terrain-materials/` (1254² PNG), delivered as 1024² WebP.
 *
 * Keys are GroundPalette ids (groundPalette.ts); a test pins that every palette has one, so a new
 * palette cannot silently fall back to another biome's ground.
 */
function albedo(id: string, hash: string, meanLinear: [number, number, number]): GroundAlbedo {
  return { url: `assets/terrain-materials-v1/${id}.webp`, hash, tileMetres: 4, meanLinear };
}

export const GROUND_ALBEDOS: Readonly<Record<string, GroundAlbedo>> = {
  // 2026-09-24 (sprint-assembly-1, AD-647): verdant-forest, open-meadow and town-packed-earth
  // swapped for the owner's 2026-09-23 batch (art-inbox/texture-gen-2026-09-23: PASS takes
  // tint-verdant-forest-b~2, tint-open-meadow-a~2, tint-town-packed-earth-b), re-encoded to
  // 1024² WebP; the old tiles' dark blotches repeated visibly at play distance. Means measured
  // from the served file (linear-light average of every pixel).
  'verdant-forest': albedo(
    'verdant-forest',
    'sha256-03bf8515c5d5f63150096ad117ccf90051ac7dff1a00720e1245870bc0e83789',
    [0.12848, 0.12062, 0.0314],
  ),
  'open-meadow': albedo(
    'open-meadow',
    'sha256-0e46dd019becb532e38c8869f9d0c529f70ae04f929be60f0c4745e4b8d75de1',
    [0.16403, 0.16011, 0.04284],
  ),
  'ember-woodland': albedo(
    'ember-woodland',
    'sha256-17d95da0c036c5862cc2552a11e6e8a8cb26e2d83171959c9696ceed0ad47581',
    [0.10794, 0.07392, 0.01915],
  ),
  'canyon-grit': albedo(
    'canyon-grit',
    'sha256-e895fb8d089a1d566bf8e7955b268cf8d2d001121d7a71c40f3faf16dfabbffd',
    [0.20704, 0.10504, 0.05255],
  ),
  'town-packed-earth': albedo(
    'town-packed-earth',
    'sha256-77dd4605f66f4577dbb3403ab37bab4eb8152a4ad9ee7a5d61a02396ab252ecb',
    [0.23899, 0.14349, 0.06477],
  ),
  'damp-mire': albedo(
    'damp-mire',
    'sha256-19ca9523e693dbb90c206e8c7a80f59c3289201e3fcc4b7b7c97165a3f6e1394',
    [0.04008, 0.04192, 0.02537],
  ),
  'neutral-flat': albedo(
    'neutral-flat',
    'sha256-1fb01e2af1c00466536aab773a83400b0dc1129f41aee0bcce1a0443ee91e48c',
    [0.17654, 0.15115, 0.11186],
  ),
  'earth-meadow': albedo(
    'earth-meadow',
    'sha256-7bfcb380b6dfb98e376295d410fdcdd813226d0b310814ef1a54fe962c4ab786',
    [0.10595, 0.12747, 0.03702],
  ),
};

/** The fallback texture — the default palette's own (`earth-meadow`). */
export const DEFAULT_GROUND_ALBEDO: GroundAlbedo = GROUND_ALBEDOS['earth-meadow'] as GroundAlbedo;

/** The texture for a resolved palette id, or the default when a palette has none. */
export function groundAlbedoFor(paletteId: string): GroundAlbedo {
  return GROUND_ALBEDOS[paletteId] ?? DEFAULT_GROUND_ALBEDO;
}

/** Served URL for an albedo under an asset base ('' = same-origin), versioned by its hash. */
export function groundAlbedoUrl(albedo: GroundAlbedo, httpBase: string): string {
  const base = httpBase.replace(/\/+$/, '');
  const path = albedo.url.replace(/^\/+/, '');
  return `${base}/${path}?v=${albedo.hash}`;
}

/**
 * A texture plus the readiness flag every ground material USING that texture samples. Shared per
 * texture, so one load flips all of its ground meshes from procedural to textured on the same frame,
 * and a slow or failed load simply leaves the procedural ground showing — never a black floor.
 */
export interface GroundAlbedoHandle {
  readonly albedo: GroundAlbedo;
  readonly texture: THREE.Texture;
  readonly ready: { value: number };
}

/**
 * Configure a loaded (or loading) texture for world-space tiling. Separated from loading so it is
 * testable without a DOM.
 */
export function prepareGroundTexture(texture: THREE.Texture): THREE.Texture {
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  // three clamps to the device maximum; a grazing view of the ground is exactly where it matters.
  texture.anisotropy = 8;
  return texture;
}

/** A texture loader, injected so the scene builder stays node-testable (no DOM in tests). */
export type GroundTextureLoad = (
  url: string,
  onLoad: () => void,
  onError: () => void,
) => THREE.Texture;

/**
 * Browser loader: three's TextureLoader. With no DOM (Game constructed headless, as the lifecycle
 * tests do) there is no image element to load into, so it returns an empty texture that never
 * reports ready — the procedural ground stays, exactly as if the load were still in flight — rather
 * than throwing from inside Game's constructor.
 */
export function createBrowserGroundTextureLoad(
  cdnFallback: AssetCdnFallback = sessionAssetCdnFallback(),
): GroundTextureLoad {
  if (typeof document === 'undefined') {
    return () => new THREE.Texture();
  }
  const loader = new THREE.TextureLoader();
  const images = new THREE.ImageLoader();
  return (url, onLoad, onError) => {
    const first = cdnFallback.rewrite(url);
    const texture = loader.load(
      first,
      () => onLoad(),
      undefined,
      (event) => {
        // S3: an image failure carries no status, so any CDN failure is retried once from the
        // game's own origin, into the SAME texture the caller already holds.
        const retry = cdnFallback.retryFrom(first, event, true);
        if (retry === null) return onError();
        images.load(
          retry,
          (image) => {
            texture.image = image;
            texture.needsUpdate = true;
            onLoad();
          },
          undefined,
          () => onError(),
        );
      },
    );
    return texture;
  };
}

/** Start loading an albedo; the handle is usable immediately and flips `ready` when uploaded. */
export function loadGroundAlbedo(
  albedo: GroundAlbedo,
  httpBase: string,
  load: GroundTextureLoad,
  onWarn?: (message: string) => void,
): GroundAlbedoHandle {
  const ready = { value: 0 };
  const url = groundAlbedoUrl(albedo, httpBase);
  const texture = prepareGroundTexture(
    load(
      url,
      () => {
        texture.needsUpdate = true;
        ready.value = 1;
      },
      () => onWarn?.(`ground texture failed to load (${url}); keeping the procedural ground`),
    ),
  );
  return { albedo, texture, ready };
}
